import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';

interface AddOnRowProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  isSelected: boolean;
  /**
   * Add-on is functionally included in the user's currently-selected tier
   * (the tier's grants fully cover what the add-on provides). When true the
   * row renders as checked + disabled and shows "Included" instead of price.
   */
  isIncluded?: boolean;
  onToggle?: (bundleId: string) => void;
  disabled?: boolean;
}

/**
 * Compact add-on display for dense surfaces like the Person Card modal and the
 * upcoming Edit Plans batch modal. One row per add-on — checkbox + name on the
 * left, "Included" or "$X/mo" on the right. Strips the description, features
 * list, and Learn-more link of `AddOnCard` for a single-line look that fits
 * inside the modal accordion.
 */
export const AddOnRow: React.FC<AddOnRowProps> = ({
  bundle: rawBundle,
  billingInterval,
  isSelected,
  isIncluded = false,
  onToggle,
  disabled = false,
}) => {
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;

  const priceCents =
    billingInterval === 'month'
      ? bundle.monthly_unit_amount
      : Math.round(bundle.yearly_unit_amount / 12);
  const formattedPrice = (priceCents / 100).toFixed(0);

  const checked = isSelected || isIncluded;
  const isDisabled = disabled || isIncluded;

  return (
    <Box
      data-testid={`addon-row-${bundle.bundle_id}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={checked}
            disabled={isDisabled}
            onChange={() => onToggle?.(bundle.bundle_id)}
          />
        }
        label={<Typography variant="body2">{bundle.name}</Typography>}
        sx={{ mx: 0, flex: 1 }}
      />
      <Typography variant="caption" color="text.secondary">
        {isIncluded ? 'Included' : `$${formattedPrice}/mo`}
      </Typography>
    </Box>
  );
};
